import { useEffect, useState, memo } from "react";
import isEqual from "lodash.isequal";
import { useTranslation } from "react-i18next";
import {
	Paper,
	Typography,
	Skeleton,
	Stack,
	IconButton,
	TextField,
	Button,
	ListItem,
	ListItemText,
	ListItemIcon,
	List,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
} from "@mui/material";
import { useColorScheme } from "@mui/material/styles";
import ReactJsonView from "@microlink/react-json-view";
import JsonEditor from "@monaco-editor/react";
import ReactDiffViewer from "react-diff-viewer-continued";
// cmps
import Toast from "../Toast";
// hooks
import useSend from "../../hooks/useSend";
// services
import User from "../../services/user";
import { icons } from "@local/theme";
// types
import type { GroupName, GatewayData, FilteredGroups } from "../../types/firmware-groups";
import type { CmdReadGroup, CmdUpdateGroup, CmdUpdateGroups } from "../../types/gupport";

type UpdateGroup = "updateGroup" | "updateGroupsByCategory" | "updateGroupsByGroups";

type Props = Readonly<{
	selectedGroupName: GroupName;
	groups: FilteredGroups;
	fetchGroups: () => void;
}>;

const parseEditorJson = (editorValue: string | undefined) => {
	try {
		return editorValue ? JSON.parse(editorValue) as GatewayData : undefined;
	} catch {
		return undefined;
	}
};

const isJsonInvalid = (parsedJson: GatewayData | null | undefined) => {
	if (!parsedJson?.apps || typeof parsedJson.apps !== "object") {
		return true;
	}
	return false;
};

const GroupData = ({ selectedGroupName, groups, fetchGroups }: Props) => {
	const send = useSend();

	const { t } = useTranslation();
	const { mode, systemMode } = useColorScheme();

	const [jsonData, setJsonData] = useState<GatewayData | undefined>(undefined);
	const [loading, setLoading] = useState(true);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editorValue, setEditorValue] = useState<string | undefined>(undefined);
	const [showDiffDialog, setShowDiffDialog] = useState(false);
	const [showSelectGroupDialog, setShowSelectGroupDialog] = useState(false);
	const [showConfirmCategoryDialog, setShowConfirmCategoryDialog] = useState(false);
	const [showUpdateGroupSuccess, setShowUpdateGroupSuccess] = useState<number | undefined>(undefined);
	const [showGenericErrorMsg, setShowGenericErrorMsg] = useState<number | undefined>(undefined);
	const [selectedGroups, setSelectedGroups] = useState<Array<GroupName>>([]);
	const [updateCategoryName, setUpdateCategoryName] = useState("");
	const [updateGroupInProgress, setUpdateGroupInProgress] = useState(false);
	const [inProgress, setInProgress] = useState(false);
	const [updateGroupType, setUpdateGroupType] = useState<UpdateGroup | undefined>(undefined);

	const parsedEditorJson = parseEditorJson(editorValue);

	useEffect(() => {
		setLoading(true);
		const cmd = {
			action: "readGroup",
			group: selectedGroupName,
		} as const satisfies CmdReadGroup;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setShowGenericErrorMsg(undefined);
				setJsonData(msg.payload.data);
			} else {
				setShowGenericErrorMsg(Date.now());
			}
			setIsEditMode(false);
			setLoading(false);
		});
	}, [selectedGroupName, send]);

	const updateGroup = () => {
		if (isJsonInvalid(parsedEditorJson)) {
			return;
		}
		setUpdateGroupInProgress(true);
		const cmd = {
			action: "updateGroup",
			group: selectedGroupName,
			groupContent: parsedEditorJson!,
		} as const satisfies CmdUpdateGroup;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setEditorValue("");
				setShowGenericErrorMsg(undefined);
				setJsonData(parsedEditorJson);
				setShowUpdateGroupSuccess(Date.now());
				fetchGroups();
			} else {
				setShowGenericErrorMsg(Date.now());
			}
			setUpdateGroupInProgress(false);
		});
	};

	const updateGroups = (groupNames: ReadonlyArray<GroupName> | undefined) => {
		if (isJsonInvalid(parsedEditorJson) || groupNames === undefined) {
			return;
		}
		setInProgress(true);
		const cmd = {
			action: "updateGroups",
			groups: groupNames,
			groupContent: parsedEditorJson!,
		} as const satisfies CmdUpdateGroups;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setEditorValue("");
				setUpdateCategoryName("");
				setShowGenericErrorMsg(undefined);
				setJsonData(parsedEditorJson);
				setShowUpdateGroupSuccess(Date.now());
				fetchGroups();
			} else {
				setShowGenericErrorMsg(Date.now());
			}
			setInProgress(false);
			setShowConfirmCategoryDialog(false);
			setShowSelectGroupDialog(false);
		}, 120000);
	};

	const showJsondataDiffDialog = (_updateGroupType: UpdateGroup) => {
		if (isEditMode) {
			setShowDiffDialog(true);
			setUpdateGroupType(_updateGroupType);
		} else {
			setIsEditMode(true);
			setEditorValue(JSON.stringify(jsonData, null, 2));
		}
	};

	const handleCancel = () => {
		setShowDiffDialog(false);
		setIsEditMode(false);
	};

	const handleConfirm = () => {
		if (updateGroupType === "updateGroupsByGroups") {
			setShowSelectGroupDialog(true);
		} else if (updateGroupType === "updateGroupsByCategory") {
			setShowConfirmCategoryDialog(true);
		} else {
			updateGroup();
		}
		setIsEditMode(false);
		setShowDiffDialog(false);
	};

	const handleToggleGroup = (groupName: GroupName) => {
		setSelectedGroups((prev) => (prev.includes(groupName)
			? prev.filter((grp) => grp !== groupName)
			: [...prev, groupName]));
	};

	const handleUpdateGroupsByCategory = () => {
		const groupNames = groups.find((group) => group.category === jsonData?.category)?.names;
		updateGroups(groupNames);
		setUpdateCategoryName("");
	};

	const handleCancelSelectGroupDialog = () => {
		setInProgress(false);
		setShowSelectGroupDialog(false);
		setSelectedGroups([]);
	};

	const handleCancelConfirmCategoryDialog = () => {
		setInProgress(false);
		setShowConfirmCategoryDialog(false);
		setUpdateCategoryName("");
	};

	const isDarkMode = (systemMode ?? mode) === "dark";
	const isUpdateGroupInvalid = isEditMode && !parsedEditorJson;

	return (
		<>
			<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
				<Typography variant="h5" gutterBottom={true}>{t("firmwareGroups.jsonData")}</Typography>
				{User.hasLevel("fw_group_manage") &&
					<IconButton
						size="small"
						onClick={() => (showJsondataDiffDialog("updateGroup"))}
						aria-label={t(isEditMode ? "firmwareGroups.saveJsonData" : "firmwareGroups.editJsonData")}
						disabled={isUpdateGroupInvalid}
					>
						{isEditMode ? <icons.Done color={isUpdateGroupInvalid ? "disabled" : "success"} sx={{ fontSize: "20px" }} /> : <icons.Edit sx={{ fontSize: "20px" }} />}
					</IconButton>
				}
			</Stack>
			{loading ?
				<Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
				: <Paper sx={{ p: 1 }}>
					{isEditMode ?
						<JsonEditor
							height="175px"
							defaultLanguage="json"
							language="json"
							value={editorValue}
							theme={isDarkMode ? "vs-dark" : "light"}
							onChange={(value) => setEditorValue(value)}
							options={{
								minimap: { enabled: false },
								formatOnPaste: true,
								formatOnType: true,
								automaticLayout: true,
								fontSize: 15,
							}}
						/> :
						<ReactJsonView
							src={jsonData ?? {}}
							name={false}
							theme={isDarkMode ? "google" : "rjv-default"}
							enableClipboard={false}
							displayDataTypes={false}
							displayObjectSize={false}
							style={{
								fontSize: "16px",
							}}
						/>
					}
				</Paper>
			}
			{isEditMode &&
				<Stack direction="row" spacing={1}>
					<Button
						variant="contained"
						disabled={!parsedEditorJson}
						onClick={() => (showJsondataDiffDialog("updateGroupsByCategory"))}
					>
						{t("firmwareGroups.actions.updateGroups.applyConfigToCategory")}
					</Button>
					<Button
						variant="contained"
						disabled={!parsedEditorJson}
						onClick={() => (showJsondataDiffDialog("updateGroupsByGroups"))}
					>
						{t("firmwareGroups.actions.updateGroups.applyConfigToGroups")}
					</Button>
				</Stack>
			}
			<Dialog
				fullWidth={true}
				open={showDiffDialog}
				onClose={handleCancel}
				maxWidth="md"
			>
				<DialogTitle>{t("firmwareGroups.actions.updateGroup.dialogTitle")}</DialogTitle>
				<DialogContent>
					<ReactDiffViewer
						oldValue={JSON.stringify(jsonData, null, 2)}
						newValue={JSON.stringify(parsedEditorJson, null, 2)}
						useDarkTheme={isDarkMode}
						leftTitle={t("firmwareGroups.actions.updateGroup.before")}
						rightTitle={t("firmwareGroups.actions.updateGroup.after")}
						showDiffOnly={false}
						styles={{ summary: { display: "none" } }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancel}>{t("dialog.cancel")}</Button>
					<Button
						disabled={(updateGroupType === "updateGroup" && isEqual(jsonData, parsedEditorJson))}
						loading={updateGroupInProgress}
						onClick={handleConfirm}
					>
						{t("dialog.confirm")}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth={true}
				open={showSelectGroupDialog}
				onClose={() => setShowSelectGroupDialog(false)}
			>
				<DialogTitle>{t("firmwareGroups.actions.updateGroups.dialogTitle")}</DialogTitle>
				<DialogContent>
					<List>
						{groups.flatMap((group) => (group.names)).map((name) => (
							<ListItem
								key={name}
								disablePadding={true}
								onClick={() => handleToggleGroup(name)}
							>
								<ListItemIcon>
									<Checkbox edge="start" checked={selectedGroups.includes(name)} />
								</ListItemIcon>
								<ListItemText primary={name} />
							</ListItem>
						))}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelSelectGroupDialog}>
						{t("dialog.cancel")}
					</Button>
					<Button loading={inProgress} onClick={() => (updateGroups(selectedGroups))}>
						{t("dialog.confirm")}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth={true}
				open={showConfirmCategoryDialog}
				onClose={() => (setShowConfirmCategoryDialog(false))}
			>
				<DialogTitle>{t("firmwareGroups.actions.updateGroups.dialogTitle")}</DialogTitle>
				<DialogContent>
					<TextField
						placeholder={t("firmwareGroups.actions.updateGroups.placeholder", { CATEGORY_NAME: jsonData?.category ?? "root" })}
						fullWidth={true}
						value={updateCategoryName}
						onChange={(event) => (setUpdateCategoryName(event.target.value))}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelConfirmCategoryDialog}>{t("dialog.cancel")}</Button>
					<Button
						disabled={jsonData?.category !== updateCategoryName}
						loading={inProgress}
						onClick={handleUpdateGroupsByCategory}
					>
						{t("dialog.confirm")}
					</Button>
				</DialogActions>
			</Dialog>
			<Toast
				autoHideDuration={6000}
				severity="error"
				open={showGenericErrorMsg}
				onClose={setShowGenericErrorMsg}
				message={t("toast.genericErrorMsg")}
			/>
			<Toast
				autoHideDuration={3000}
				severity="success"
				open={showUpdateGroupSuccess}
				onClose={setShowUpdateGroupSuccess}
				message={t("firmwareGroups.actions.updateGroup.successMsg")}
			/>
		</>
	);
};

export default memo(GroupData);
